import React from 'react';
import { t } from '@lingui/macro';

import { Box, Button, Heading, Paragraph } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import Modal from '@rover/kibble/official-patterns/Modal';
import type { HomeAddressUpdate } from '@rover/rsdk/src/apiClient/latest/model/homeAddressUpdate';
import type { UserInformation } from '@rover/rsdk/src/apiClient/latest/model/userInformation';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import { KibbleAddressForm } from '@rover/shared/js/components/KibbleAddressForm';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';

import useHomeAddress from '../hooks/useHomeAddress';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (newAddressData: UserInformationAddress) => void;
  forceUseHomeAddress?: boolean;
};

export default function HomeEditModal({
  isOpen,
  onClose,
  onEdit,
  forceUseHomeAddress,
}: Props): JSX.Element {
  const { address, saveAddress, isLoading, submissionErrors } = useHomeAddress(forceUseHomeAddress);
  const { user } = useCurrentUserShim();
  const isMobile = isMobileBrowser();

  const isProvider = (currentUser?: UserInformation): boolean => {
    return currentUser?.userMode === 'provider';
  };

  const handleFormSubmit = (data: HomeAddressUpdate): void => {
    saveAddress({
      data,
      onSuccess: (newAddressData) => {
        if (newAddressData && onEdit) {
          onEdit(newAddressData);
        }
        onClose();
      },
    });
  };

  const renderHeader = (): JSX.Element => (
    <>
      {isMobile && <Heading size="300" mb="2x">{t`Edit home address`}</Heading>}
      <Paragraph size={isMobile ? '100' : '200'}>
        {t`Update your current home address to find the best sitters nearby.`}
      </Paragraph>
    </>
  );

  const display = isMobile
    ? { Component: BottomSheet, props: { hideCloseIcon: true } }
    : { Component: Modal, props: { showCloseIcon: true, headerText: t`Edit home address` } };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <display.Component isOpen={isOpen} onRequestClose={onClose} {...display.props}>
        {renderHeader()}
        <KibbleAddressForm
          onSubmit={handleFormSubmit}
          defaultValues={
            address ? (address as Partial<typeof address> & Record<string, unknown>) : undefined
          }
          disableCountry={isProvider(user)}
          isLoading={isLoading}
          submissionErrors={submissionErrors}
        />
        <Box display="flex" justifyContent="center">
          <Button variant="flat" onClick={onClose} width="100%" size="small">
            {t`Cancel`}
          </Button>
        </Box>
      </display.Component>
    </Box>
  );
}
